import { useContext, useEffect } from "preact/hooks";
import { useOnceRef } from "./once-ref";
import { Signal, signal } from "@preact/signals";
import {
	Atom,
	ComputedAtom,
	createAtom,
	createComputedAtom,
	createReaction,
	Reaction,
} from "fluidstate";
import { Context } from "preact";

/**
 * A Preact hook that bridges `fluidstate` reactivity with `@preact/signals`.
 * It returns a `get` function that can be used to create memoized Preact signals
 * from `fluidstate` reactive state. This is the most performant way to use
 * `fluidstate` in Preact, as it avoids component re-renders and updates only
 * the specific parts of the DOM that depend on the state.
 *
 * @param Context - Optional. A Preact Context object. If provided, the state
 * from this context will be available to the selector functions passed to `get`.
 * This is typically used with `createReactiveSetup`.
 * @returns A `get` function that takes a selector and returns a Preact Signal.
 */
export const useSignals = <ContextValue>(Context?: Context<ContextValue>) => {
	// A ref to store the context object and its latest value.
	// This helps in detecting when the context value itself changes.
	const context = useOnceRef(() => ({
		Context,
		contextValue: null as ContextValue,
	}));

	// Get the current context value. If a context was provided, use `useContext`.
	const contextValue = (
		context.current.Context ? useContext(context.current.Context) : null
	) as ContextValue;
	// Check if the context value has changed since the last render.
	const isContextValueChanged = contextValue !== context.current.contextValue;
	context.current.contextValue = contextValue;

	// This ref holds all the reactive primitives for each `get` call.
	// We use arrays and rely on the call index, similar to how React hooks work.
	const data = useOnceRef<{
		latestIndex: number;
		// The selector functions passed to `get`.
		fns: Array<(value: ContextValue) => unknown>;
		// `fluidstate` atoms to track changes in the selector functions themselves.
		fnAtoms: Atom[];
		// The `@preact/signals` that are returned by `get`.
		resultSignals: Signal[];
		// `fluidstate` computed atoms that bridge `fluidstate` reactivity to Preact signals.
		// Each computed atom runs a selector and updates the corresponding Preact signal.
		computeds: ComputedAtom<unknown>[];
		// `fluidstate` reactions to keep the computed atoms "active".
		reactions: Reaction[];
	}>(() => ({
		latestIndex: 0,
		fns: [],
		fnAtoms: [],
		resultSignals: [],
		computeds: [],
		reactions: [],
	}));

	// Reset the index at the beginning of each render. The `get` function will increment it for each call.
	data.current.latestIndex = -1;

	// This effect runs after every render to clean up any reactive primitives
	// that are no longer used (e.g., due to conditional rendering).
	useEffect(() => {
		const fnsLength = data.current.latestIndex + 1;

		// If the number of `get` calls decreased, stop the reactions for the orphaned signals.
		for (let i = fnsLength; i < data.current.reactions.length; i++) {
			data.current.reactions[i].stop();
		}

		// Truncate the arrays to the new length to release memory.
		data.current.fns.length = fnsLength;
		data.current.fnAtoms.length = fnsLength;
		data.current.resultSignals.length = fnsLength;
		data.current.reactions.length = fnsLength;
	});

	// This effect runs only once on mount and cleans up all reactions when the component unmounts.
	useEffect(() => {
		return () => {
			for (let i = 0; i < data.current.reactions.length; i++) {
				data.current.reactions[i].stop();
			}
		};
	}, []);

	/**
	 * Creates and returns a memoized Preact signal for a given piece of `fluidstate` data.
	 * @param fn A selector function that accesses `fluidstate` reactive state and returns a value.
	 * @returns A Preact `Signal` that will automatically update when the underlying state changes.
	 */
	return <T>(fn: (value: ContextValue) => T) => {
		const currentIndex = ++data.current.latestIndex;

		// Check if we've already set up the reactive primitives for this index.
		if (data.current.fns[currentIndex]) {
			// If so, this is a subsequent render. Check if the selector function or context value has changed.
			if (data.current.fns[currentIndex] !== fn || isContextValueChanged) {
				// If they have changed, update the function and notify `fluidstate` that this dependency has changed.
				// This will trigger the computed atom to re-evaluate.
				data.current.fns[currentIndex] = fn;
				data.current.fnAtoms[currentIndex].reportChanged();
			}
		} else {
			// This is the first time this `get` call is being made (at this index).
			// We need to set up the entire reactive chain.
			data.current.fns[currentIndex] = fn;
			data.current.fnAtoms[currentIndex] = createAtom(
				`useSignals/fnAtom@${currentIndex}`
			);
			data.current.resultSignals[currentIndex] = signal<unknown>(null);

			// The core of the bridge: a `fluidstate` computed that, when run,
			// executes the selector `fn` and updates the Preact signal's value.
			// It depends on the `fnAtom` (for selector changes) and any reactive
			// state accessed inside `fn`.
			data.current.computeds[currentIndex] = createComputedAtom(
				`useSignals/computed@${currentIndex}`,
				() => {
					data.current.fnAtoms[currentIndex].reportObserved();
					let result = data.current.fns[currentIndex](
						context.current.contextValue
					);
					if (Array.isArray(result)) {
						result.length;
						result = Object.create(result);
					}
					data.current.resultSignals[currentIndex].value = result;
				}
			);

			// A reaction is needed to keep the computed atom "hot" or "active".
			// This ensures that the computed re-evaluates automatically whenever
			// its dependencies change, thus keeping the Preact signal in sync.
			data.current.reactions[currentIndex] = createReaction(() => {
				data.current.computeds[currentIndex].get();
			});
		}

		// Return the memoized Preact signal for this index.
		return data.current.resultSignals[currentIndex] as Signal<T>;
	};
};
